import { lazy, Suspense, useMemo } from "react"
import { Outlet, RouteObject } from "react-router"
import { SkeletonCardList } from "@/components/shared/loading/card.tsx"
import { SkeletonPageLoading } from "@/components/shared/loading/page.tsx"
import CogsRoute from "./cogs/route.tsx"
import CogsSetupRoute from "./cogs/setup/route.tsx"
import CogsSetupDirectCostRoute from "./cogs/setup/direct-cost/route.tsx"
import CogsHistoricalRoute from "./cogs/historical/route.tsx"
import CogsForecastRoute from "./cogs/forecast/route.tsx"
import CogsDirectCostsPeriodRoute from "./cogs/[period]/direct-cost/route.tsx"
import OpexSetupRoute from "@/routes/app/financial-model/ecommerce/opex/setup/route.tsx"
import OpexHistoricalRoute from "@/routes/app/financial-model/ecommerce/opex/historical/route.tsx"
import OpexForecastRoute from "@/routes/app/financial-model/ecommerce/opex/forecast/route.tsx"
import { PersonnelHomeRoute } from "./personnel/route.tsx"
import { TotalResultSkeleton } from "@/components/shared/loading/tables.tsx"
import { SkeletonAccordion } from "@/components/shared/loading/accordion.tsx"
import { importOrReload } from "@/lib/utils/dynamic-import.ts"
import TwcRoute from "./twc/route.tsx"
import OwcRoute from "./owc/route.tsx"
import CapitalStructureRoute from "./capital-structure/route.tsx"
import { twcLoader } from "@/feature/financial-model/ecommerce/twc/loader/index.ts"
import { owcLoader } from "@/feature/financial-model/ecommerce/owc/loader/index.ts"
import CogsSetupDirectLabourRoute from "@/routes/app/financial-model/ecommerce/cogs/setup/direct-labour/route.tsx"
import { CogsDirectLabourPeriodRoute } from "@/routes/app/financial-model/ecommerce/cogs/[period]/direct-labour/route.tsx"
import { provisionsLoader } from "@/feature/financial-model/ecommerce/provisions/loader/index.ts"

const LazySingleFinancialModel = lazy(() =>
  importOrReload(() =>
    import(`../[id]/route.tsx`).then((mod) => ({
      default: mod.SingleFinancialModelRoute,
    }))
  )
)

const LazyRevenuesRoute = lazy(() =>
  importOrReload(() =>
    import(`./revenues/route.tsx`).then((mod) => ({
      default: mod.RevenuesRoute,
    }))
  )
)

const LazyRevenuesSetUpRoute = lazy(() =>
  importOrReload(() =>
    import(`./revenues/set-up/route.tsx`).then((mod) => ({
      default: mod.RevenuesSetUpRoute,
    }))
  )
)

const LazyRevenuesHistoricalRoute = lazy(() =>
  importOrReload(() =>
    import(`./revenues/historical/route.tsx`).then((mod) => ({
      default: mod.RevenuesHistoricalRoute,
    }))
  )
)

const LazyRevenuesForecastRoute = lazy(() =>
  importOrReload(() =>
    import(`./revenues/forecast/route.tsx`).then((mod) => ({
      default: mod.RevenuesForecastRoute,
    }))
  )
)

const LazyCustomerSegmentsRoute = lazy(() =>
  importOrReload(() =>
    import(`./revenues/set-up/customer-segments/route.tsx`).then((mod) => ({
      default: mod.CustomerSegmentsRoute,
    }))
  )
)

const LazyProductSetupRoute = lazy(() =>
  importOrReload(() =>
    import(`./revenues/set-up/product/route.tsx`).then((mod) => ({
      default: mod.ProductSetupRoute,
    }))
  )
)

const LazyOrderTypesRoute = lazy(() =>
  importOrReload(() =>
    import(`./revenues/set-up/orders/route.tsx`).then((mod) => ({
      default: mod.OrdersRoute,
    }))
  )
)

const LazyMarketingRoute = lazy(() =>
  importOrReload(() =>
    import(`./revenues/set-up/marketing/route.tsx`).then((mod) => ({
      default: mod.MarketingRoute,
    }))
  )
)

const LazySalesRoute = lazy(() =>
  importOrReload(() =>
    import(`./revenues/set-up/sales/route.tsx`).then((mod) => ({
      default: mod.SalesRoute,
    }))
  )
)

const LazyOrganicRoute = lazy(() =>
  importOrReload(() =>
    import(`./revenues/set-up/organic/route.tsx`).then((mod) => ({
      default: mod.OrganicRoute,
    }))
  )
)

const LazyRevenuesHistoricalPricesRoute = lazy(() =>
  importOrReload(() =>
    import(`./revenues/[period]/prices/route.tsx`).then((mod) => ({
      default: mod.RevenuesHistoricalPricesRoute,
    }))
  )
)

const LazyCustomerSegmentByPeriodRoute = lazy(() =>
  importOrReload(() =>
    import(`./revenues/[period]/segments/route.tsx`).then((mod) => ({
      default: mod.CustomerSegmentByPeriodRoute,
    }))
  )
)

const LazyOrdersByPeriodRoute = lazy(() =>
  importOrReload(() => import(`./revenues/[period]/orders/route.tsx`))
)

const LazyMarketingByPeriodRoute = lazy(() =>
  importOrReload(() =>
    import(`./revenues/[period]/marketing/route.tsx`).then((mod) => ({
      default: mod.MarketingByPeriodRoute,
    }))
  )
)

const LazySalesByPeriodRoute = lazy(() =>
  importOrReload(() =>
    import(`./revenues/[period]/sales/route.tsx`).then((mod) => ({
      default: mod.SalesByPeriodRoute,
    }))
  )
)

const LazyOrganicByPeriodRoute = lazy(() =>
  importOrReload(() =>
    import(`./revenues/[period]/organic/route.tsx`).then((mod) => ({
      default: mod.OrganicByPeriodRoute,
    }))
  )
)

const LazyRevenuesTotalsRoute = lazy(() =>
  importOrReload(() => import(`./revenues/totals/route.tsx`))
)

const LazyPersonnelSetUpRoute = lazy(() =>
  importOrReload(() =>
    import(`./personnel/set-up/route.tsx`).then((mod) => ({
      default: mod.PersonnelSetUpRoute,
    }))
  )
)

const LazyPersonnelTotalRoute = lazy(() =>
  importOrReload(() =>
    import(`./personnel/total/route.tsx`).then((mod) => ({
      default: mod.PersonnelTotalRoute,
    }))
  )
)

const LazyPersonnelHistoricalRoute = lazy(() =>
  importOrReload(() =>
    import(`./personnel/historical/route.tsx`).then((mod) => ({
      default: mod.PersonnelHistoricalRoute,
    }))
  )
)

const LazyPersonnelForecastRoute = lazy(() =>
  importOrReload(() =>
    import(`./personnel/forecast/route.tsx`).then((mod) => ({
      default: mod.PersonnelForecastRoute,
    }))
  )
)

const PersonnelPeriodLazyRoute = lazy(() =>
  importOrReload(() =>
    import(`./personnel/[period]/route.tsx`).then((mod) => ({
      default: mod.PersonnelPeriodRoute,
    }))
  )
)

const OpexHomeRoute = lazy(() =>
  importOrReload(() => import(`./opex/route.tsx`))
)

const OpexPeriodRoute = lazy(() =>
  importOrReload(() => import(`./opex/[period]/route.tsx`))
)

const OpexTotalsRoute = lazy(() =>
  importOrReload(() => import(`./opex/totals/route.tsx`))
)

const LazyCogsTotalsDirectCostRoute = lazy(() =>
  importOrReload(() => import(`./cogs/totals/route.tsx`))
)

const OicRoute = lazy(() => importOrReload(() => import(`./oic/route.tsx`)))

const OicSetupRoute = lazy(() =>
  importOrReload(() => import(`./oic/setup/route.tsx`))
)

const OicHistoricalRoute = lazy(() =>
  importOrReload(() => import(`./oic/historical/route.tsx`))
)

const OicForecastRoute = lazy(() =>
  importOrReload(() => import(`./oic/forecast/route.tsx`))
)

const OicTotalRoute = lazy(() =>
  importOrReload(() => import(`./oic/total/route.tsx`))
)

const OicSetupRouteWithContext = lazy(() =>
  importOrReload(() => import(`./oic/setup/[context]/route.tsx`))
)

const OicPeriodContextRoute = lazy(() =>
  importOrReload(() => import(`./oic/[period]/[context]/route.tsx`))
)

const TotalOicContextRoute = lazy(() =>
  importOrReload(() => import(`./oic/total/[context]/route.tsx`))
)

const LazyFinancialTables = lazy(() =>
  importOrReload(() => import(`./financial-tables/route.tsx`))
)
// TWC

const LazyTwcSetupReceivableRoute = lazy(() =>
  importOrReload(() => import(`./twc/setup/receivable/route.tsx`))
)

const LazyTwcReceivablePeriodRoute = lazy(() =>
  importOrReload(() => import(`./twc/[period]/receivable/route.tsx`))
)

const LazyTwcSetupRoute = lazy(() =>
  importOrReload(() => import(`./twc/setup/route.tsx`))
)

const LazyTwcHistoricalRoute = lazy(() =>
  importOrReload(() => import(`./twc/historical/route.tsx`))
)

const LazyTwcForecastRoute = lazy(() =>
  importOrReload(() => import(`./twc/forecast/route.tsx`))
)

const LazyTwcSetupInventoryRoute = lazy(() =>
  importOrReload(() => import(`./twc/setup/inventory/route.tsx`))
)

const LazyTwcInventoryPeriodRoute = lazy(() =>
  importOrReload(() => import(`./twc/[period]/inventory/route.tsx`))
)

const LazyTwcPayablePeriodRoute = lazy(() =>
  importOrReload(() => import(`./twc/[period]/payable/route.tsx`))
)

const LazyTwcSetupPayableRoute = lazy(() =>
  importOrReload(() => import(`./twc/setup/payable/route.tsx`))
)

const LazyTwcTotalsRoute = lazy(() =>
  importOrReload(() => import(`./twc/totals/route.tsx`))
)

const LazyOwcSetupRoute = lazy(() =>
  importOrReload(() => import(`./owc/setup/route.tsx`))
)

const LazyOwcHistoricalRoute = lazy(() =>
  importOrReload(() => import(`./owc/historical/route.tsx`))
)

const LazyOwcTotalsRoute = lazy(() =>
  importOrReload(() => import(`./owc/totals/route.tsx`))
)

const LazyOwcSetupCurrentAssetsRoute = lazy(() =>
  importOrReload(() => import(`./owc/setup/current-assets/route.tsx`))
)

const LazyOwcSetupCurrentLiabilitiesRoute = lazy(() =>
  importOrReload(() => import(`./owc/setup/current-liabilities/route.tsx`))
)
const LazyOwcSetupTaxesRoute = lazy(() =>
  importOrReload(() => import(`./owc/setup/taxes/route.tsx`))
)

const LazyOwcCurrentAssetsPeriodRoute = lazy(() =>
  importOrReload(() => import(`./owc/[period]/current-assets/route.tsx`))
)

const LazyOwcCurrentLiabilitiesPeriodRoute = lazy(() =>
  importOrReload(() => import(`./owc/[period]/current-liabilities/route.tsx`))
)

const LazyOwcTaxesPeriodRoute = lazy(() =>
  importOrReload(() => import(`./owc/[period]/taxes/route.tsx`))
)

const LazyOwcForecastRoute = lazy(() =>
  importOrReload(() => import(`./owc/forecast/route.tsx`))
)

const LazyFixedAssetsRoute = lazy(() =>
  importOrReload(() => import(`./fixed-assets/route.tsx`))
)

const LazyFixedAssetsSetupRoute = lazy(() =>
  importOrReload(() => import(`./fixed-assets/setup/route.tsx`))
)

const LazyFixedAssetsHistoricalRoute = lazy(() =>
  importOrReload(() => import(`./fixed-assets/historical/route.tsx`))
)

const LazyFixedAssetsForecastRoute = lazy(() =>
  importOrReload(() => import(`./fixed-assets/forecast/route.tsx`))
)

const LazyFixedAssetsTotalsRoute = lazy(() =>
  importOrReload(() => import(`./fixed-assets/totals/route.tsx`))
)

const LazyVatRoute = lazy(() => importOrReload(() => import(`./vat/route.tsx`)))

const LazyVatSetupRoute = lazy(() =>
  importOrReload(() => import(`./vat/setup/route.tsx`))
)

const LazyVatPeriodRoute = lazy(() =>
  importOrReload(() => import(`./vat/[period]/[type]/route.tsx`))
)

const LazyVatTotalsRoute = lazy(() =>
  importOrReload(() => import(`./vat/totals/route.tsx`))
)

const LazyVatHistoricalRoute = lazy(() =>
  importOrReload(() => import(`./vat/historical/route.tsx`))
)

const LazyVatForecastRoute = lazy(() =>
  importOrReload(() => import(`./vat/forecast/route.tsx`))
)

const LazyTaxesRoute = lazy(() =>
  importOrReload(() => import(`./taxes/route.tsx`))
)

const LazyTaxesSetupRoute = lazy(() =>
  importOrReload(() => import(`./taxes/setup/route.tsx`))
)

const LazyTaxesHistoricalRoute = lazy(() =>
  importOrReload(() => import(`./taxes/historical/route.tsx`))
)

const LazyTaxesForecastRoute = lazy(() =>
  importOrReload(() => import(`./taxes/forecast/route.tsx`))
)

const LazyTaxesTotalsRoute = lazy(() =>
  importOrReload(() => import(`./taxes/totals/route.tsx`))
)

const LazyProvisionsRoute = lazy(() =>
  importOrReload(() => import(`./provisions/route.tsx`))
)

const LazyProvisionsSetupRoute = lazy(() =>
  importOrReload(() => import(`./provisions/setup/route.tsx`))
)

const LazyProvisionsHistoricalRoute = lazy(() =>
  importOrReload(() => import(`./provisions/historical/route.tsx`))
)

const LazyProvisionsForecastRoute = lazy(() =>
  importOrReload(() => import(`./provisions/forecast/route.tsx`))
)

const LazyProvisionsTotalsRoute = lazy(() =>
  importOrReload(() => import(`./provisions/totals/route.tsx`))
)

const LazyProvisionsPeriodBadDebtRoute = lazy(() =>
  importOrReload(() => import(`./provisions/[period]/bad_debt/route.tsx`))
)

const LazyProvisionsPeriodOtherRoute = lazy(() =>
  importOrReload(() => import(`./provisions/[period]/other/route.tsx`))
)

const LazyProvisionsPeriodInventoryRoute = lazy(() =>
  importOrReload(() => import(`./provisions/[period]/inventory/route.tsx`))
)

// Capital Structure Routes
const LazyCapitalStructureSetupRoute = lazy(() =>
  importOrReload(() => import(`./capital-structure/setup/route.tsx`))
)

const LazyCapitalStructureHistoricalRoute = lazy(() =>
  importOrReload(() => import(`./capital-structure/historical/route.tsx`))
)

const LazyCapitalStructureForecastRoute = lazy(() =>
  importOrReload(() => import(`./capital-structure/forecast/route.tsx`))
)

const LazyCapitalStructureTotalsRoute = lazy(() =>
  importOrReload(() => import(`./capital-structure/totals/route.tsx`))
)

const LazyCapitalStructureConvertibleSetupRoute = lazy(() =>
  importOrReload(
    () => import(`./capital-structure/setup/convertible/route.tsx`)
  )
)

const LazyCapitalStructureDebtSetupRoute = lazy(() =>
  importOrReload(() => import(`./capital-structure/setup/debt/route.tsx`))
)

const LazyCapitalStructureEquitySetupRoute = lazy(() =>
  importOrReload(() => import(`./capital-structure/setup/equity/route.tsx`))
)

const LazyCapitalStructureGrantSetupRoute = lazy(() =>
  importOrReload(() => import(`./capital-structure/setup/grant/route.tsx`))
)

const LazyCapitalStructureConvertiblePeriodRoute = lazy(() =>
  importOrReload(
    () => import(`./capital-structure/[period]/convertible/route.tsx`)
  )
)

const LazyCapitalStructureDebtPeriodRoute = lazy(() =>
  importOrReload(() => import(`./capital-structure/[period]/debt/route.tsx`))
)

const LazyCapitalStructureEquityPeriodRoute = lazy(() =>
  importOrReload(() => import(`./capital-structure/[period]/equity/route.tsx`))
)

const LazyCapitalStructureGrantPeriodRoute = lazy(() =>
  importOrReload(() => import(`./capital-structure/[period]/grant/route.tsx`))
)

export const useEcommerceRoutes = () => {
  const opexRoutes: RouteObject[] = useMemo(
    () => [
      {
        path: `ecommerce/opex`,
        element: <OpexHomeRoute />,
        hydrateFallbackElement: <SkeletonPageLoading />,
        children: [
          {
            index: true,
            element: (
              <Suspense fallback={<SkeletonAccordion defaultOpen />}>
                <OpexSetupRoute />
              </Suspense>
            ),
          },
          {
            path: `historical-data`,
            element: (
              <Suspense fallback={<SkeletonCardList num={3} type="card" />}>
                <OpexHistoricalRoute />
              </Suspense>
            ),
          },
          {
            path: `forecast-data`,
            element: (
              <Suspense fallback={<SkeletonCardList num={3} type="card" />}>
                <OpexForecastRoute />
              </Suspense>
            ),
          },
          {
            path: `total`,
            element: (
              <Suspense fallback={<TotalResultSkeleton />}>
                <OpexTotalsRoute />
              </Suspense>
            ),
          },
        ],
      },
      {
        path: `ecommerce/opex/:period/:opex_category_id`,
        element: (
          <Suspense fallback={<SkeletonPageLoading />}>
            <OpexPeriodRoute />
          </Suspense>
        ),
      },
    ],
    []
  )

  const personnelRoutes = useMemo(
    () => [
      {
        path: `ecommerce/personnel`,
        element: <PersonnelHomeRoute />,
        hydrateFallbackElement: <SkeletonPageLoading />,
        children: [
          {
            index: true,
            element: (
              <Suspense fallback={<SkeletonAccordion defaultOpen />}>
                <LazyPersonnelSetUpRoute />
              </Suspense>
            ),
          },
          {
            path: `historical-data`,
            element: (
              <Suspense fallback={<SkeletonCardList num={3} type="card" />}>
                <LazyPersonnelHistoricalRoute />
              </Suspense>
            ),
          },
          {
            path: `forecast-data`,
            element: (
              <Suspense fallback={<SkeletonCardList num={3} type="card" />}>
                <LazyPersonnelForecastRoute />
              </Suspense>
            ),
          },
          {
            path: `total`,
            element: (
              <Suspense fallback={<TotalResultSkeleton />}>
                <LazyPersonnelTotalRoute />
              </Suspense>
            ),
          },
        ],
      },
      {
        path: `ecommerce/personnel/:period/:department`,
        element: (
          <Suspense fallback={<SkeletonPageLoading />}>
            <PersonnelPeriodLazyRoute />
          </Suspense>
        ),
      },
    ],
    []
  )

  const bottomUpRoutes = useMemo(
    () => [
      {
        path: `ecommerce/revenues/marketing`,
        element: (
          <Suspense fallback={<SkeletonPageLoading />}>
            <LazyMarketingRoute />
          </Suspense>
        ),
        hydrateFallbackElement: <SkeletonPageLoading />,
      },
      {
        path: `ecommerce/revenues/sales`,
        element: (
          <Suspense fallback={<SkeletonPageLoading />}>
            <LazySalesRoute />
          </Suspense>
        ),
        hydrateFallbackElement: <SkeletonPageLoading />,
      },
      {
        path: `ecommerce/revenues/organic`,
        element: (
          <Suspense fallback={<SkeletonPageLoading />}>
            <LazyOrganicRoute />
          </Suspense>
        ),
        hydrateFallbackElement: <SkeletonPageLoading />,
      },
      {
        path: `ecommerce/revenues/:period/marketing`,
        element: (
          <Suspense fallback={<SkeletonPageLoading />}>
            <LazyMarketingByPeriodRoute />
          </Suspense>
        ),
      },
      {
        path: `ecommerce/revenues/:period/sales`,
        element: (
          <Suspense fallback={<SkeletonPageLoading />}>
            <LazySalesByPeriodRoute />
          </Suspense>
        ),
      },
      {
        path: `ecommerce/revenues/:period/organic`,
        element: (
          <Suspense fallback={<SkeletonPageLoading />}>
            <LazyOrganicByPeriodRoute />
          </Suspense>
        ),
      },
    ],
    []
  )

  const oicRoutes = useMemo(
    () => [
      {
        path: `ecommerce/oic`,
        element: (
          <Suspense fallback={<SkeletonPageLoading />}>
            <OicRoute />
          </Suspense>
        ),
        hydrateFallbackElement: <SkeletonPageLoading />,
        children: [
          {
            index: true,
            element: (
              <Suspense fallback={<SkeletonPageLoading />}>
                <OicSetupRoute />
              </Suspense>
            ),
          },
          {
            path: `historical-data`,
            element: (
              <Suspense fallback={<SkeletonCardList num={3} type="card" />}>
                <OicHistoricalRoute />
              </Suspense>
            ),
          },
          {
            path: `forecast-data`,
            element: (
              <Suspense fallback={<SkeletonCardList num={3} type="card" />}>
                <OicForecastRoute />
              </Suspense>
            ),
          },
          {
            path: `total`,
            element: (
              <Suspense fallback={<SkeletonPageLoading />}>
                <OicTotalRoute />
              </Suspense>
            ),
          },
        ],
      },
      {
        path: `ecommerce/oic/setup/:context`,
        hydrateFallbackElement: <SkeletonPageLoading />,
        element: (
          <Suspense fallback={<SkeletonPageLoading />}>
            <OicSetupRouteWithContext />
          </Suspense>
        ),
      },
      {
        path: `ecommerce/oic/:period/:context`,
        element: (
          <Suspense fallback={<SkeletonPageLoading />}>
            <OicPeriodContextRoute />
          </Suspense>
        ),
      },
      {
        path: `ecommerce/oic/totals/:context`,
        element: (
          <Suspense fallback={<SkeletonPageLoading />}>
            <TotalOicContextRoute />
          </Suspense>
        ),
      },
    ],
    []
  )

  const twcRoutes: RouteObject[] = useMemo(
    () => [
      {
        path: `ecommerce/trade-working-capital`,
        element: (
          <Suspense fallback={<SkeletonPageLoading />}>
            <TwcRoute />
          </Suspense>
        ),
        hydrateFallbackElement: <SkeletonPageLoading />,
        children: [
          {
            index: true,
            element: (
              <Suspense fallback={<SkeletonCardList num={3} type="card" />}>
                <LazyTwcSetupRoute />
              </Suspense>
            ),
          },
          {
            path: `historical-data`,
            element: (
              <Suspense fallback={<SkeletonCardList num={3} type="card" />}>
                <LazyTwcHistoricalRoute />
              </Suspense>
            ),
          },
          {
            path: `forecast-data`,
            element: (
              <Suspense fallback={<SkeletonCardList num={3} type="card" />}>
                <LazyTwcForecastRoute />
              </Suspense>
            ),
          },
          {
            path: `total`,
            element: (
              <Suspense fallback={<TotalResultSkeleton />}>
                <LazyTwcTotalsRoute />
              </Suspense>
            ),
          },
        ],
      },
      {
        path: `ecommerce/trade-working-capital/inventory`,
        loader: twcLoader,
        element: <Outlet />,
        hydrateFallbackElement: <SkeletonPageLoading />,
        children: [
          {
            index: true,
            element: (
              <Suspense fallback={<SkeletonPageLoading />}>
                <LazyTwcSetupInventoryRoute />
              </Suspense>
            ),
          },
          {
            path: `setup`,
            loader: twcLoader,
            element: (
              <Suspense fallback={<SkeletonPageLoading />}>
                <LazyTwcSetupInventoryRoute />
              </Suspense>
            ),
          },
          {
            path: `:period`,
            loader: twcLoader,
            element: (
              <Suspense fallback={<SkeletonPageLoading />}>
                <LazyTwcInventoryPeriodRoute />
              </Suspense>
            ),
          },
        ],
      },
      {
        path: `ecommerce/trade-working-capital/receivable`,
        loader: twcLoader,
        element: <Outlet />,
        hydrateFallbackElement: <SkeletonPageLoading />,
        children: [
          {
            index: true,
            element: (
              <Suspense fallback={<SkeletonPageLoading />}>
                <LazyTwcSetupReceivableRoute />
              </Suspense>
            ),
          },
          {
            path: `setup`,
            loader: twcLoader,
            element: (
              <Suspense fallback={<SkeletonPageLoading />}>
                <LazyTwcSetupReceivableRoute />
              </Suspense>
            ),
          },
          {
            path: `:period`,
            loader: twcLoader,
            element: (
              <Suspense fallback={<SkeletonPageLoading />}>
                <LazyTwcReceivablePeriodRoute />
              </Suspense>
            ),
          },
        ],
      },
      {
        path: `ecommerce/trade-working-capital/payable`,
        loader: twcLoader,
        element: <Outlet />,
        hydrateFallbackElement: <SkeletonPageLoading />,
        children: [
          {
            index: true,
            element: (
              <Suspense fallback={<SkeletonPageLoading />}>
                <LazyTwcSetupPayableRoute />
              </Suspense>
            ),
          },
          {
            path: `setup`,
            loader: twcLoader,
            element: (
              <Suspense fallback={<SkeletonPageLoading />}>
                <LazyTwcSetupPayableRoute />
              </Suspense>
            ),
          },
          {
            path: `:period`,
            loader: twcLoader,
            element: (
              <Suspense fallback={<SkeletonPageLoading />}>
                <LazyTwcPayablePeriodRoute />
              </Suspense>
            ),
          },
        ],
      },
    ],
    []
  )

  const owcRoutes: RouteObject[] = useMemo(
    () => [
      {
        path: `ecommerce/other-working-capital`,
        element: (
          <Suspense fallback={<SkeletonPageLoading />}>
            <OwcRoute />
          </Suspense>
        ),
        hydrateFallbackElement: <SkeletonPageLoading />,
        children: [
          {
            index: true,
            element: (
              <Suspense fallback={<SkeletonCardList num={3} type="card" />}>
                <LazyOwcSetupRoute />
              </Suspense>
            ),
          },
          {
            path: `historical-data`,
            element: (
              <Suspense fallback={<SkeletonCardList num={3} type="card" />}>
                <LazyOwcHistoricalRoute />
              </Suspense>
            ),
          },
          {
            path: `forecast-data`,
            element: (
              <Suspense fallback={<SkeletonCardList num={3} type="card" />}>
                <LazyOwcForecastRoute />
              </Suspense>
            ),
          },
          {
            path: `total`,
            element: (
              <Suspense fallback={<TotalResultSkeleton />}>
                <LazyOwcTotalsRoute />
              </Suspense>
            ),
          },
        ],
      },
      {
        path: `ecommerce/other-working-capital/current-assets`,
        loader: owcLoader,
        element: <Outlet />,
        hydrateFallbackElement: <SkeletonPageLoading />,
        children: [
          {
            index: true,
            element: (
              <Suspense fallback={<SkeletonPageLoading />}>
                <LazyOwcSetupCurrentAssetsRoute />
              </Suspense>
            ),
          },
          {
            path: `setup`,
            loader: owcLoader,
            element: (
              <Suspense fallback={<SkeletonPageLoading />}>
                <LazyOwcSetupCurrentAssetsRoute />
              </Suspense>
            ),
          },
          {
            path: `:period`,
            loader: owcLoader,
            element: (
              <Suspense fallback={<SkeletonPageLoading />}>
                <LazyOwcCurrentAssetsPeriodRoute />
              </Suspense>
            ),
          },
        ],
      },
      {
        path: `ecommerce/other-working-capital/current-liabilities`,
        loader: owcLoader,
        element: <Outlet />,
        hydrateFallbackElement: <SkeletonPageLoading />,
        children: [
          {
            index: true,
            element: (
              <Suspense fallback={<SkeletonPageLoading />}>
                <LazyOwcSetupCurrentLiabilitiesRoute />
              </Suspense>
            ),
          },
          {
            path: `setup`,
            loader: owcLoader,
            element: (
              <Suspense fallback={<SkeletonPageLoading />}>
                <LazyOwcSetupCurrentLiabilitiesRoute />
              </Suspense>
            ),
          },
          {
            path: `:period`,
            loader: owcLoader,
            element: (
              <Suspense fallback={<SkeletonPageLoading />}>
                <LazyOwcCurrentLiabilitiesPeriodRoute />
              </Suspense>
            ),
          },
        ],
      },
      {
        path: `ecommerce/other-working-capital/taxes`,
        loader: owcLoader,
        element: <Outlet />,
        hydrateFallbackElement: <SkeletonPageLoading />,
        children: [
          {
            index: true,
            element: (
              <Suspense fallback={<SkeletonPageLoading />}>
                <LazyOwcSetupTaxesRoute />
              </Suspense>
            ),
          },
          {
            path: `setup`,
            loader: owcLoader,
            element: (
              <Suspense fallback={<SkeletonPageLoading />}>
                <LazyOwcSetupTaxesRoute />
              </Suspense>
            ),
          },
          {
            path: `:period`,
            loader: owcLoader,
            element: (
              <Suspense fallback={<SkeletonPageLoading />}>
                <LazyOwcTaxesPeriodRoute />
              </Suspense>
            ),
          },
        ],
      },
    ],
    []
  )

  const vatRoutes: RouteObject[] = useMemo(
    () => [
      {
        path: `ecommerce/vat`,
        element: (
          <Suspense fallback={<SkeletonPageLoading />}>
            <LazyVatRoute />
          </Suspense>
        ),
        hydrateFallbackElement: <SkeletonPageLoading />,
        children: [
          {
            index: true,
            element: (
              <Suspense fallback={<SkeletonAccordion defaultOpen />}>
                <LazyVatSetupRoute />
              </Suspense>
            ),
          },
          {
            path: `setup`,
            element: (
              <Suspense fallback={<SkeletonAccordion defaultOpen />}>
                <LazyVatSetupRoute />
              </Suspense>
            ),
          },
          {
            path: `historical-data`,
            element: (
              <Suspense fallback={<SkeletonCardList num={3} type="card" />}>
                <LazyVatHistoricalRoute />
              </Suspense>
            ),
          },
          {
            path: `forecast-data`,
            element: (
              <Suspense fallback={<SkeletonCardList num={3} type="card" />}>
                <LazyVatForecastRoute />
              </Suspense>
            ),
          },
          {
            path: `total`,
            element: (
              <Suspense fallback={<SkeletonPageLoading />}>
                <LazyVatTotalsRoute />
              </Suspense>
            ),
          },
        ],
      },
      {
        path: `ecommerce/vat/:period/:type`,
        element: (
          <Suspense fallback={<SkeletonPageLoading />}>
            <LazyVatPeriodRoute />
          </Suspense>
        ),
      },
    ],
    []
  )

  const fixedAssetsRoutes: RouteObject[] = useMemo(
    () => [
      {
        path: `ecommerce/fixed-assets`,
        element: (
          <Suspense fallback={<SkeletonPageLoading />}>
            <LazyFixedAssetsRoute />
          </Suspense>
        ),
        hydrateFallbackElement: <SkeletonPageLoading />,
        children: [
          {
            index: true,
            element: (
              <Suspense fallback={<SkeletonCardList num={3} type="card" />}>
                <LazyFixedAssetsSetupRoute />
              </Suspense>
            ),
          },
          {
            path: `historical-data`,
            element: (
              <Suspense fallback={<SkeletonCardList num={3} type="card" />}>
                <LazyFixedAssetsHistoricalRoute />
              </Suspense>
            ),
          },
          {
            path: `forecast-data`,
            element: (
              <Suspense fallback={<SkeletonCardList num={3} type="card" />}>
                <LazyFixedAssetsForecastRoute />
              </Suspense>
            ),
          },
          {
            path: `total`,
            element: (
              <Suspense fallback={<SkeletonCardList num={3} type="card" />}>
                <LazyFixedAssetsTotalsRoute />
              </Suspense>
            ),
          },
        ],
      },
    ],
    []
  )
  const cogsRoutes = useMemo(
    () => [
      {
        path: `ecommerce/cogs`,
        element: (
          <Suspense fallback={<SkeletonPageLoading />}>
            <CogsRoute />
          </Suspense>
        ),
        hydrateFallbackElement: <SkeletonPageLoading />,
        children: [
          {
            index: true,
            element: (
              <Suspense fallback={<SkeletonCardList num={2} type="card" />}>
                <CogsSetupRoute />
              </Suspense>
            ),
          },
          {
            path: `historical-data`,
            element: (
              <Suspense fallback={<SkeletonCardList num={2} type="card" />}>
                <CogsHistoricalRoute />
              </Suspense>
            ),
          },
          {
            path: `forecast-data`,
            element: (
              <Suspense fallback={<SkeletonCardList num={2} type="card" />}>
                <CogsForecastRoute />
              </Suspense>
            ),
          },
          {
            path: `total`,
            element: (
              <Suspense fallback={<SkeletonPageLoading />}>
                <LazyCogsTotalsDirectCostRoute />
              </Suspense>
            ),
          },
        ],
      },
      {
        path: `ecommerce/cogs/totals/direct-costs`,
        element: (
          <Suspense fallback={<SkeletonPageLoading />}>
            <LazyCogsTotalsDirectCostRoute />
          </Suspense>
        ),
      },
      {
        path: `ecommerce/cogs/setup/direct-costs`,
        element: (
          <Suspense fallback={<SkeletonPageLoading />}>
            <CogsSetupDirectCostRoute />
          </Suspense>
        ),
      },
      {
        path: `ecommerce/cogs/setup/direct-labour`,
        element: (
          <Suspense fallback={<SkeletonPageLoading />}>
            <CogsSetupDirectLabourRoute />
          </Suspense>
        ),
      },
      {
        path: `ecommerce/cogs/:period/direct-costs`,
        element: (
          <Suspense fallback={<SkeletonPageLoading />}>
            <CogsDirectCostsPeriodRoute />
          </Suspense>
        ),
      },
      {
        path: `ecommerce/cogs/:period/direct-labour`,
        element: (
          <Suspense fallback={<SkeletonPageLoading />}>
            <CogsDirectLabourPeriodRoute />
          </Suspense>
        ),
      },
    ],
    []
  )

  const taxesRoutes: RouteObject[] = useMemo(
    () => [
      {
        path: `ecommerce/corporate-taxes`,
        element: (
          <Suspense fallback={<SkeletonPageLoading />}>
            <LazyTaxesRoute />
          </Suspense>
        ),
        hydrateFallbackElement: <SkeletonPageLoading />,
        children: [
          {
            index: true,
            element: <LazyTaxesSetupRoute />,
          },
          {
            path: `setup`,
            element: <LazyTaxesSetupRoute />,
          },
          {
            path: `historical-data`,
            element: <LazyTaxesHistoricalRoute />,
          },
          {
            path: `forecast-data`,
            element: <LazyTaxesForecastRoute />,
          },
          {
            path: `total`,
            element: <LazyTaxesTotalsRoute />,
          },
        ],
      },
    ],
    []
  )

  const provisionsRoutes: RouteObject[] = useMemo(
    () => [
      {
        path: `ecommerce/provisions`,
        loader: provisionsLoader,
        element: (
          <Suspense fallback={<SkeletonPageLoading />}>
            <LazyProvisionsRoute />
          </Suspense>
        ),
        hydrateFallbackElement: <SkeletonPageLoading />,
        children: [
          {
            index: true,
            loader: provisionsLoader,
            element: (
              <Suspense fallback={<SkeletonPageLoading />}>
                <LazyProvisionsSetupRoute />
              </Suspense>
            ),
          },
          {
            path: `setup`,
            loader: provisionsLoader,
            element: (
              <Suspense fallback={<SkeletonPageLoading />}>
                <LazyProvisionsSetupRoute />
              </Suspense>
            ),
          },
          {
            path: `historical-data`,
            element: <LazyProvisionsHistoricalRoute />,
          },
          {
            path: `forecast-data`,
            element: <LazyProvisionsForecastRoute />,
          },
          {
            path: `total`,
            element: <LazyProvisionsTotalsRoute />,
          },
        ],
      },
      {
        path: `ecommerce/provisions/:period/bad-debt`,
        element: <LazyProvisionsPeriodBadDebtRoute />,
      },
      {
        path: `ecommerce/provisions/:period/other`,
        element: <LazyProvisionsPeriodOtherRoute />,
      },
      {
        path: `ecommerce/provisions/:period/inventory`,
        element: <LazyProvisionsPeriodInventoryRoute />,
      },
    ],
    []
  )

  const capitalStructureRoutes: RouteObject[] = useMemo(
    () => [
      {
        path: `ecommerce/capital-structure`,
        element: (
          <Suspense fallback={<SkeletonPageLoading />}>
            <CapitalStructureRoute />
          </Suspense>
        ),
        hydrateFallbackElement: <SkeletonPageLoading />,
        children: [
          {
            index: true,
            element: (
              <Suspense fallback={<SkeletonCardList num={3} type="card" />}>
                <LazyCapitalStructureSetupRoute />
              </Suspense>
            ),
          },
          {
            path: `historical-data`,
            element: (
              <Suspense fallback={<SkeletonCardList num={3} type="card" />}>
                <LazyCapitalStructureHistoricalRoute />
              </Suspense>
            ),
          },
          {
            path: `forecast-data`,
            element: (
              <Suspense fallback={<SkeletonCardList num={3} type="card" />}>
                <LazyCapitalStructureForecastRoute />
              </Suspense>
            ),
          },
          {
            path: `total`,
            element: (
              <Suspense fallback={<TotalResultSkeleton />}>
                <LazyCapitalStructureTotalsRoute />
              </Suspense>
            ),
          },
        ],
      },
      {
        path: `ecommerce/capital-structure/convertible`,
        element: <Outlet />,
        hydrateFallbackElement: <SkeletonPageLoading />,
        children: [
          {
            index: true,
            element: (
              <Suspense fallback={<SkeletonPageLoading />}>
                <LazyCapitalStructureConvertibleSetupRoute />
              </Suspense>
            ),
          },
          {
            path: `setup`,
            element: (
              <Suspense fallback={<SkeletonPageLoading />}>
                <LazyCapitalStructureConvertibleSetupRoute />
              </Suspense>
            ),
          },
          {
            path: `:period`,
            element: (
              <Suspense fallback={<SkeletonPageLoading />}>
                <LazyCapitalStructureConvertiblePeriodRoute />
              </Suspense>
            ),
          },
        ],
      },
      {
        path: `ecommerce/capital-structure/debt`,
        element: <Outlet />,
        hydrateFallbackElement: <SkeletonPageLoading />,
        children: [
          {
            index: true,
            element: (
              <Suspense fallback={<SkeletonPageLoading />}>
                <LazyCapitalStructureDebtSetupRoute />
              </Suspense>
            ),
          },
          {
            path: `setup`,
            element: (
              <Suspense fallback={<SkeletonPageLoading />}>
                <LazyCapitalStructureDebtSetupRoute />
              </Suspense>
            ),
          },
          {
            path: `:period`,
            element: (
              <Suspense fallback={<SkeletonPageLoading />}>
                <LazyCapitalStructureDebtPeriodRoute />
              </Suspense>
            ),
          },
        ],
      },
      {
        path: `ecommerce/capital-structure/equity`,
        element: <Outlet />,
        hydrateFallbackElement: <SkeletonPageLoading />,
        children: [
          {
            index: true,
            element: (
              <Suspense fallback={<SkeletonPageLoading />}>
                <LazyCapitalStructureEquitySetupRoute />
              </Suspense>
            ),
          },
          {
            path: `setup`,
            element: (
              <Suspense fallback={<SkeletonPageLoading />}>
                <LazyCapitalStructureEquitySetupRoute />
              </Suspense>
            ),
          },
          {
            path: `:period`,
            element: (
              <Suspense fallback={<SkeletonPageLoading />}>
                <LazyCapitalStructureEquityPeriodRoute />
              </Suspense>
            ),
          },
        ],
      },
      {
        path: `ecommerce/capital-structure/grant`,
        element: <Outlet />,
        hydrateFallbackElement: <SkeletonPageLoading />,
        children: [
          {
            index: true,
            element: (
              <Suspense fallback={<SkeletonPageLoading />}>
                <LazyCapitalStructureGrantSetupRoute />
              </Suspense>
            ),
          },
          {
            path: `setup`,
            element: (
              <Suspense fallback={<SkeletonPageLoading />}>
                <LazyCapitalStructureGrantSetupRoute />
              </Suspense>
            ),
          },
          {
            path: `:period`,
            element: (
              <Suspense fallback={<SkeletonPageLoading />}>
                <LazyCapitalStructureGrantPeriodRoute />
              </Suspense>
            ),
          },
        ],
      },
    ],
    []
  )

  const ecommerceRoutes = useMemo(
    () => [
      {
        path: `ecommerce`,
        element: <LazySingleFinancialModel />,
      },
      {
        path: `ecommerce/financial-tables`,
        element: <LazyFinancialTables />,
        hydrateFallbackElement: <SkeletonPageLoading />,
      },
      {
        path: `ecommerce/revenues`,
        element: <LazyRevenuesRoute />,
        hydrateFallbackElement: <SkeletonPageLoading />,
        children: [
          {
            index: true,
            element: (
              <Suspense fallback={<SkeletonCardList num={3} type="card" />}>
                <LazyRevenuesSetUpRoute />
              </Suspense>
            ),
          },
          {
            path: `historical-data`,
            element: (
              <Suspense fallback={<SkeletonCardList num={3} type="card" />}>
                <LazyRevenuesHistoricalRoute />
              </Suspense>
            ),
          },
          {
            path: `forecast-data`,
            element: (
              <Suspense fallback={<SkeletonCardList num={3} type="card" />}>
                <LazyRevenuesForecastRoute />
              </Suspense>
            ),
          },
          {
            path: `total`,
            element: (
              <Suspense fallback={<TotalResultSkeleton />}>
                <LazyRevenuesTotalsRoute />
              </Suspense>
            ),
          },
        ],
      },

      {
        path: `ecommerce/revenues/customer-segments`,
        element: (
          <Suspense fallback={<SkeletonPageLoading />}>
            <LazyCustomerSegmentsRoute />
          </Suspense>
        ),
        hydrateFallbackElement: <SkeletonPageLoading />,
      },
      {
        path: `ecommerce/revenues/products`,
        element: (
          <Suspense fallback={<SkeletonPageLoading />}>
            <LazyProductSetupRoute />
          </Suspense>
        ),
        hydrateFallbackElement: <SkeletonPageLoading />,
      },
      {
        path: `ecommerce/revenues/orders`,
        element: <LazyOrderTypesRoute />,
        hydrateFallbackElement: <SkeletonPageLoading />,
      },
      {
        path: `ecommerce/revenues/:period/prices`,
        element: (
          <Suspense fallback={<SkeletonPageLoading />}>
            <LazyRevenuesHistoricalPricesRoute />
          </Suspense>
        ),
        hydrateFallbackElement: <SkeletonPageLoading />,
      },
      {
        path: `ecommerce/revenues/:period/customer-segments`,
        element: (
          <Suspense fallback={<SkeletonPageLoading />}>
            <LazyCustomerSegmentByPeriodRoute />
          </Suspense>
        ),
        hydrateFallbackElement: <SkeletonPageLoading />,
      },
      {
        path: `ecommerce/revenues/:period/orders`,
        element: <LazyOrdersByPeriodRoute />,
        hydrateFallbackElement: <SkeletonPageLoading />,
      },
      ...bottomUpRoutes,
      ...personnelRoutes,
      ...cogsRoutes,
      ...opexRoutes,
      ...oicRoutes,
      ...twcRoutes,
      ...owcRoutes,
      ...fixedAssetsRoutes,
      ...vatRoutes,
      ...taxesRoutes,
      ...provisionsRoutes,
      ...capitalStructureRoutes,
    ],
    [
      bottomUpRoutes,
      personnelRoutes,
      cogsRoutes,
      opexRoutes,
      oicRoutes,
      twcRoutes,
      owcRoutes,
      fixedAssetsRoutes,
      vatRoutes,
      taxesRoutes,
      provisionsRoutes,
      capitalStructureRoutes,
    ]
  )

  return ecommerceRoutes
}
